import type { AppConfig } from '@/types/auth';
// Type-only import: brings in the class type for `client` without pulling the
// module into the bundle. The runtime import below is dynamic.
import type { AwsRum as RumClient } from 'aws-rum-web';
import { sanitiseAttrs, type TrackAttrs } from '@/lib/analytics-attrs';

// A union rather than free strings: the funnel query matches these names
// exactly, so a typo would silently drop a step instead of failing.
export type AnalyticsEvent =
  | 'dialog_loaded'
  | 'dialog_intro_continued'
  | 'dialog_started'
  | 'dialog_reached_review'
  | 'dialog_completed';

let client: RumClient | null = null;
let started = false;

const MAX_BUFFERED = 20;
const buffered: Array<{ event: AnalyticsEvent; attrs: Record<string, string | number> }> = [];

// Fire-and-forget. The SDK is imported dynamically so it stays out of the main
// bundle and off the authenticated pages, and loading it never blocks paint.
export function initAnalytics(config: AppConfig): void {
  if (started) return;

  const appMonitorId = config.analytics?.appMonitorId;
  const { identityPoolId, region } = config.auth.cognito;
  // Unconfigured is a supported state, not an error: local dev and any
  // environment without an app monitor simply record nothing.
  if (!appMonitorId || !identityPoolId || !region) return;

  started = true;

  void (async () => {
    try {
      const { AwsRum } = await import('aws-rum-web');

      client = new AwsRum(appMonitorId, config.app.version, region, {
        // No guestRoleArn. Supplying it alongside identityPoolId selects
        // Cognito's basic authflow, which this identity pool rejects
        // (allow_classic_flow = false). Omitting it uses the enhanced flow,
        // which resolves the guest role from the pool's roles attachment.
        identityPoolId,
        // Required despite the region argument above. The SDK's default
        // config pre-fills `endpoint` with us-west-2 and the constructor only
        // derives it from the region when no endpoint is set, so without this
        // every request is signed for our region but sent to us-west-2, which
        // rejects it (403) — and after the first rejection the client
        // disables itself, so the failure is silent.
        endpoint: `https://dataplane.rum.${region}.amazonaws.com`,
        sessionSampleRate: 1,
        allowCookies: false,
        // The real path is /dialog/<invite-token> and the default pageIdFormat
        // is 'PATH', so leaving this off would write a capability credential
        // into CloudWatch Logs.
        disableAutoPageView: true,
        // Must be set explicitly. The SDK default is
        // [errors, performance, http, replay] — http would capture the invite
        // token in /d/<token> request URLs, and replay would run rrweb and
        // record the respondent's screen as they type. Sentry owns errors.
        telemetries: [],
        recordResourceUrl: false,
      });

      // Static page id, for the same reason as disableAutoPageView.
      client.recordPageView('/dialog');

      for (const queued of buffered.splice(0)) {
        client.recordEvent(queued.event, queued.attrs);
      }
    } catch (err) {
      // Telemetry is best-effort: a failing tracker must never break a
      // respondent's dialog. Resetting the flags stops track() buffering
      // events that could never flush, and lets a remount retry the init —
      // a re-fired dialog_loaded is safe because the funnel counts distinct
      // sessionIds.
      console.warn('Analytics init failed', err);
      buffered.length = 0;
      client = null;
      started = false;
    }
  })();
}

export function track(event: AnalyticsEvent, attrs?: TrackAttrs): void {
  if (!started) return;

  const safe = sanitiseAttrs(attrs as Record<string, unknown> | undefined);

  try {
    if (client) {
      client.recordEvent(event, safe);
    } else if (buffered.length < MAX_BUFFERED) {
      // Events fired before the dynamic import resolves are queued and flushed
      // on init. Bounded so a failed init can't grow an unbounded queue.
      buffered.push({ event, attrs: safe });
    }
  } catch (err) {
    console.warn('Analytics event failed', err);
  }
}
